import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Search, Filter, X, ClipboardCheck, ArrowRight, CheckCircle2 } from 'lucide-react';

const CLASSES = ['Preparatory', 'Lower Nursery', 'Upper Nursery', 'Transition', 'Class I', 'Class II',
  'Class III', 'Class IV', 'Class V', 'Class VI', 'Class VII', 'Class VIII', 'Class IX'];

const daysPending = (c) => {
  const from = c.submitted_for_approval_at || c.updated_at;
  if (!from) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(from).getTime()) / 86400000));
};

export default function PendingApprovals() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: '', class_name: '', test_result: '', test_status: '', min_days: '' });

  useEffect(() => {
    api.get('/cases', { params: { status: 'Awaiting Rector Approval' } })
      .then((r) => setCases(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => cases.filter((c) => {
    const q = f.q.trim().toLowerCase();
    if (q && !(`${c.applicant_name} ${c.case_number} ${c.contact_number}`).toLowerCase().includes(q)) return false;
    if (f.class_name && c.class_name !== f.class_name) return false;
    const tr = (c.test?.result?.result) || '';
    if (f.test_result && tr !== f.test_result) return false;
    const ts = (c.test?.status) || '';
    if (f.test_status && ts !== f.test_status) return false;
    if (f.min_days && daysPending(c) < Number(f.min_days)) return false;
    return true;
  }), [cases, f]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const activeCount = Object.values(f).filter(Boolean).length;
  const inputCls = 'rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#475569] outline-none focus:border-action';

  return (
    <div className="space-y-5" data-testid="pending-approvals-page">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy text-gold"><ClipboardCheck size={20} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#172033]">Pending Approvals</h1>
          <p className="text-sm text-[#64748B]">{filtered.length} case{filtered.length !== 1 ? 's' : ''} awaiting your decision</p>
        </div>
      </div>

      <div className="rounded-xl border border-[#E2E8F0] bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input data-testid="approvals-search" value={f.q} onChange={set('q')} placeholder="Search name, case ID, mobile…"
              className="w-full rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] py-2 pl-9 pr-3 text-sm outline-none focus:border-action focus:bg-white" />
          </div>
          <select data-testid="approvals-filter-class" value={f.class_name} onChange={set('class_name')} className={inputCls}>
            <option value="">All Classes</option>
            {CLASSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="approvals-filter-testresult" value={f.test_result} onChange={set('test_result')} className={inputCls}>
            <option value="">Test Result: Any</option>
            <option value="Pass">Pass</option>
            <option value="Fail">Fail</option>
          </select>
          <select data-testid="approvals-filter-teststatus" value={f.test_status} onChange={set('test_status')} className={inputCls}>
            <option value="">Test Status: Any</option>
            <option value="Completed">Completed</option>
            <option value="Scheduled">Scheduled</option>
          </select>
          <select data-testid="approvals-filter-days" value={f.min_days} onChange={set('min_days')} className={inputCls}>
            <option value="">Days Pending: Any</option>
            <option value="1">1+ days</option>
            <option value="3">3+ days</option>
            <option value="7">7+ days</option>
          </select>
          {activeCount > 0 && (
            <button data-testid="approvals-clear" onClick={() => setF({ q: '', class_name: '', test_result: '', test_status: '', min_days: '' })}
              className="flex items-center gap-1 text-xs font-medium text-[#DC2626] hover:underline"><X size={13} /> Clear</button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#94A3B8]">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-[#E2E8F0] bg-white py-16 text-center" data-testid="approvals-empty">
          <CheckCircle2 size={40} className="text-[#16A34A]" />
          <p className="mt-3 font-heading text-lg font-semibold text-[#172033]">All admission cases have been reviewed.</p>
          <p className="mt-1 text-sm text-[#64748B]">There are no cases awaiting your decision right now.</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((c) => {
            const dp = daysPending(c);
            return (
              <div key={c.case_id} data-testid={`approval-card-${c.case_number}`} className="flex flex-col rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-gold hover:shadow-md">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-heading text-base font-bold text-[#172033]">{c.applicant_name}</p>
                    <p className="font-mono text-xs text-action">{c.case_number}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${dp >= 7 ? 'bg-[#FEE2E2] text-[#DC2626]' : dp >= 3 ? 'bg-[#FEF3C7] text-[#D97706]' : 'bg-[#F1F5F9] text-[#64748B]'}`}>{dp}d pending</span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-y-1.5 text-xs text-[#475569]">
                  <span>DOB: <b>{c.dob || '—'}</b></span>
                  <span>Class: <b>{c.class_name}</b></span>
                  <span>Prev. School: <b className="truncate">{c.previous_school || '—'}</b></span>
                  <span>Test: <b>{c.test?.status || 'N/A'}</b></span>
                  <span>Result: <b>{c.test?.result?.result || 'N/A'}</b></span>
                  <span>Recommend: <b>{c.office_recommendation || '—'}</b></span>
                </div>
                <p className="mt-2 text-[11px] text-[#94A3B8]">Submitted {new Date(c.submitted_for_approval_at || c.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                <button data-testid={`review-decide-${c.case_number}`} onClick={() => navigate(`/cases/${c.case_id}?tab=Approval`)}
                  className="mt-3 flex items-center justify-center gap-1.5 rounded-lg bg-navy py-2.5 text-sm font-semibold text-white transition hover:bg-navy-dark">
                  Review &amp; Decide <ArrowRight size={15} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
